import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Monitor, MapPin, Router, Search, LayoutGrid, Plus, X,
  Printer, Projector, Wifi, Server, HardDrive, Cpu, CircleDot,
} from "lucide-react";
import camApi, { STATUS_META } from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";
import AssetDrawer, { PickerSelect } from "@/components/cam/AssetDrawer";

// Pick an icon for an infrastructure device from its category / name.
function infraIcon(name = "", cName = "") {
  const s = `${name} ${cName}`.toLowerCase();
  if (/(switch|router|firewall|network|patch)/.test(s)) return Router;
  if (/(wifi|access point|wireless)/.test(s)) return Wifi;
  if (/(projector|beamer)/.test(s)) return Projector;
  if (/(printer|scanner|xerox)/.test(s)) return Printer;
  if (/(server|nas|storage)/.test(s)) return Server;
  if (/(nvr|dvr|disk|drive)/.test(s)) return HardDrive;
  return Cpu;
}

const ATTENTION = new Set(["in_repair", "in_maintenance"]);
const needsAttention = (a) => a && (ATTENTION.has(a.status) || ["poor", "damaged"].includes(a.condition));

export default function LabView() {
  const { tree, context, can, roomName, catName } = useCam();
  const inchargeLabs = context?.lab_incharge_ids || [];
  const firstLab = inchargeLabs[0] || tree.labs[0]?.id || "";
  const [labId, setLabId] = useState(String(firstLab || ""));
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [placeSeat, setPlaceSeat] = useState(null);
  const canPlace = can.admin || can.store;

  const load = async (id) => {
    if (!id) { setLayout(null); return; }
    setLoading(true);
    try { setLayout(await camApi.labLayout(id)); }
    catch (e) { toast.error(e.message); setLayout(null); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load(labId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labId]);

  const lab = tree.labs.find((l) => String(l.id) === String(labId));

  const stats = useMemo(() => {
    if (!layout?.seats) return null;
    const occupied = layout.seats.filter((s) => s.asset).length;
    const all = [...layout.seats.map((s) => s.asset).filter(Boolean), ...(layout.infra || [])];
    return {
      seats: layout.seats.length,
      occupied,
      free: layout.seats.length - occupied,
      infra: (layout.infra || []).length,
      attention: all.filter(needsAttention).length,
    };
  }, [layout]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <LayoutGrid className="h-4 w-4 text-cobalt-500" />
        <div className="overline mr-1">Lab blueprint</div>
        <PickerSelect
          placeholder="Select a lab"
          value={String(labId)}
          onChange={(v) => setLabId(v)}
          options={tree.labs.map((l) => ({ value: String(l.id), label: l.name }))}
          className="min-w-[190px]"
        />
        {lab && <span className="text-sm text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{roomName(lab.room_id) || "—"}</span>}
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2.5">
          <Stat label="Seats" value={stats.seats} tint="#0654FC" />
          <Stat label="Occupied" value={stats.occupied} tint="#0654FC" />
          <Stat label="Free" value={stats.free} tint="#64748b" />
          <Stat label="Devices" value={stats.infra} tint="#1BA85C" />
          <Stat label="Needs attention" value={stats.attention} tint={stats.attention ? "#ef4444" : "#64748b"} pulse={stats.attention > 0} />
        </div>
      )}

      {loading ? (
        <div className="glass rounded-2xl p-16 text-center text-sm text-muted-foreground">Loading layout…</div>
      ) : !labId ? (
        <div className="glass rounded-2xl p-16 text-center text-sm text-muted-foreground">No labs yet. Create one under Locations, then generate its seat grid.</div>
      ) : layout && (!layout.rows || !layout.cols || !layout.seats?.length) ? (
        <EmptyLayout labId={labId} canPlace={canPlace} onGenerated={() => load(labId)} />
      ) : layout ? (
        <>
          <Blueprint
            layout={layout}
            catName={catName}
            onSeat={(seat) => { if (seat.asset) setDrawerId(seat.asset.id); else if (canPlace) setPlaceSeat(seat); }}
            onInfra={(a) => setDrawerId(a.id)}
          />
          <Legend />
        </>
      ) : null}

      <AssetDrawer assetId={drawerId} open={!!drawerId} onOpenChange={(o) => !o && setDrawerId(null)} onChanged={() => load(labId)} />
      {placeSeat && (
        <PlaceDialog seat={placeSeat} labId={labId} infra={layout?.infra || []}
          onClose={() => setPlaceSeat(null)} onPlaced={() => { setPlaceSeat(null); load(labId); }} />
      )}
    </div>
  );
}

function Stat({ label, value, tint, pulse }) {
  return (
    <div className="glass rounded-xl px-3.5 py-2.5 relative overflow-hidden">
      <div className="absolute left-0 top-0 h-full w-1" style={{ background: tint }} />
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="metric text-2xl font-display leading-tight flex items-center gap-1.5" style={{ color: tint }}>
        {value}
        {pulse && <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />}
      </div>
    </div>
  );
}

// The room: whiteboard/front bar, an infrastructure wall, the desk grid, and an
// instructor station + a door with a swing arc.
function Blueprint({ layout, onSeat, onInfra, catName }) {
  const cols = layout.cols || 1;
  const rows = layout.rows || 1;
  const seatsByPos = useMemo(() => {
    const m = {};
    for (const s of layout.seats) m[`${s.row_num}-${s.col_num}`] = s;
    return m;
  }, [layout.seats]);

  return (
    <div className="glass-strong rounded-3xl p-4 sm:p-6 relative">
      {/* Infrastructure wall */}
      <div className="rounded-2xl border border-dashed border-cobalt-500/40 bg-gradient-to-br from-cobalt-500/[0.05] to-transparent p-3.5 mb-5">
        <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <Router className="h-3.5 w-3.5 text-cobalt-500" />Infrastructure &amp; network devices
        </div>
        {layout.infra?.length ? (
          <div className="flex flex-wrap gap-2">
            {layout.infra.map((a) => {
              const Icon = infraIcon(a.name, catName(a.category_id));
              const color = STATUS_META[a.status]?.color || "#64748b";
              return (
                <button key={a.id} onClick={() => onInfra(a)}
                  className="tap-shrink group inline-flex items-center gap-2 rounded-xl border bg-background/80 px-2.5 py-2 text-xs hover:border-cobalt-500 hover:shadow-md transition-all">
                  <span className="grid place-items-center h-7 w-7 rounded-lg" style={{ background: `${color}1a`, color }}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-left min-w-0">
                    <span className="block font-medium truncate max-w-[130px]">{a.name}</span>
                    <span className="block text-mono text-[10px] text-muted-foreground">{a.asset_tag}</span>
                  </span>
                  {needsAttention(a) && <CircleDot className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                </button>
              );
            })}
          </div>
        ) : <p className="text-xs text-muted-foreground">Assets in this lab without a seat (switches, projectors, printers) appear here.</p>}
      </div>

      {/* Room with walls */}
      <div className="relative rounded-2xl border-2 border-foreground/15 bg-[repeating-linear-gradient(45deg,transparent,transparent_11px,rgba(0,0,0,0.015)_11px,rgba(0,0,0,0.015)_12px)] p-3 sm:p-5 overflow-x-auto touch-scroll">
        <div className="mx-auto w-fit min-w-full sm:min-w-0">
          {/* Whiteboard / front */}
          <div className="mb-4 flex justify-center">
            <div className="h-6 w-2/3 max-w-[420px] rounded-md bg-gradient-to-b from-zinc-100 to-zinc-200 dark:from-zinc-700 dark:to-zinc-800 border border-black/10 shadow-inner grid place-items-center">
              <span className="text-[9px] uppercase tracking-[0.35em] text-muted-foreground">Whiteboard . Front</span>
            </div>
          </div>

          <div className="grid gap-2 sm:gap-2.5 mx-auto w-fit" style={{ gridTemplateColumns: `repeat(${cols}, minmax(46px, 1fr))` }}>
            {Array.from({ length: rows }).map((_, r) =>
              Array.from({ length: cols }).map((__, c) => {
                const seat = seatsByPos[`${r + 1}-${c + 1}`];
                if (!seat) return <div key={`${r}-${c}`} />;
                return <Seat key={seat.id} seat={seat} onClick={() => onSeat(seat)} />;
              })
            )}
          </div>
        </div>
      </div>

      {/* Instructor + door */}
      <div className="mt-4 flex items-stretch gap-3">
        <div className="flex-1 rounded-2xl border bg-black/[0.02] dark:bg-white/[0.03] px-4 py-3 text-xs text-muted-foreground flex items-center gap-2">
          <span className="h-6 w-10 rounded-md border border-black/15 bg-background inline-block" />
          Instructor station
        </div>
        <div className="relative rounded-2xl border px-4 py-3 text-xs text-muted-foreground flex items-center gap-2 overflow-hidden">
          <DoorArc />
          <span className="relative z-10">Entry</span>
        </div>
      </div>
    </div>
  );
}

function DoorArc() {
  return (
    <svg viewBox="0 0 40 40" className="absolute -left-1 -bottom-1 h-12 w-12 text-cobalt-500/40" fill="none" aria-hidden>
      <path d="M4 36 A32 32 0 0 1 36 4" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" />
      <line x1="4" y1="36" x2="4" y2="8" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

// A desk with a monitor and a chair notch. Occupied desks carry the asset's status
// as a top accent + a corner dot; empty desks invite placement.
function Seat({ seat, onClick }) {
  const a = seat.asset;
  const color = a ? STATUS_META[a.status]?.color || "#64748b" : null;
  const attn = needsAttention(a);
  return (
    <button
      onClick={onClick}
      title={a ? `${a.name} (${a.asset_tag}) . ${STATUS_META[a.status]?.label || a.status}` : `Seat ${seat.label} - empty, tap to place`}
      className={`tap-shrink group relative rounded-lg border flex flex-col items-center justify-center gap-1 py-2.5 transition-all duration-150
        ${a
          ? "bg-background hover:-translate-y-0.5 hover:shadow-lg"
          : "border-dashed bg-transparent text-muted-foreground/40 hover:border-cobalt-500/60 hover:text-cobalt-500 hover:bg-cobalt-500/[0.04]"}`}
      style={a ? { borderColor: `${color}66`, boxShadow: `inset 0 2px 0 ${color}` } : undefined}
    >
      {a ? (
        <>
          <span className="relative grid place-items-center h-7 w-9 rounded-md border border-black/10 bg-gradient-to-b from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-900" style={{ color }}>
            <Monitor className="h-4 w-4" />
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full border-2 border-background" style={{ background: color }} />
          </span>
          <span className="h-1 w-5 rounded-full bg-black/10 dark:bg-white/15" />
          <span className="text-[9px] leading-none text-muted-foreground">{seat.label}</span>
          {attn && <span className="absolute top-1 left-1 h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />}
        </>
      ) : (
        <>
          <Plus className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
          <span className="text-[9px] leading-none">{seat.label}</span>
        </>
      )}
    </button>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-1">
      {Object.entries(STATUS_META).map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="h-2 w-2 rounded-full" style={{ background: v.color }} />{v.label}
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <span className="h-2 w-2 rounded-full border border-dashed border-muted-foreground/60" />Empty seat
      </span>
      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />Needs attention
      </span>
    </div>
  );
}

function EmptyLayout({ labId, canPlace, onGenerated }) {
  const [rows, setRows] = useState(5);
  const [cols, setCols] = useState(6);
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try { const r = await camApi.genSeats(labId, { rows: Number(rows), cols: Number(cols) }); toast.success(`${r.generated} seats created`); onGenerated(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="glass rounded-2xl p-10 text-center max-w-md mx-auto">
      <LayoutGrid className="h-9 w-9 mx-auto text-muted-foreground/40" />
      <h3 className="font-display text-lg tracking-tight mt-3">No seat layout yet</h3>
      <p className="text-sm text-muted-foreground mt-1.5">
        {canPlace ? "Generate a seat grid to start placing PCs on the blueprint." : "Ask a CAM admin to generate this lab's seat grid."}
      </p>
      {canPlace && (
        <div className="flex items-end justify-center gap-2 mt-4">
          <div><label className="text-xs">Rows</label><Input type="number" min={1} max={60} value={rows} onChange={(e) => setRows(e.target.value)} className="h-9 mt-1 w-20" /></div>
          <div><label className="text-xs">Cols</label><Input type="number" min={1} max={60} value={cols} onChange={(e) => setCols(e.target.value)} className="h-9 mt-1 w-20" /></div>
          <Button onClick={gen} disabled={busy} className="bg-cobalt-500 hover:bg-cobalt-600">{busy ? "..." : "Generate"}</Button>
        </div>
      )}
    </div>
  );
}

// Place an existing asset onto the tapped seat.
function PlaceDialog({ seat, labId, infra, onClose, onPlaced }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    setSearching(true);
    try {
      const r = await camApi.assets({ q, status: "in_store" });
      setResults((r.assets || []).filter((a) => !a.assigned_seat_id));
    } catch (e) { toast.error(e.message); } finally { setSearching(false); }
  };
  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const place = async (assetId) => {
    setBusy(true);
    try { await camApi.placeAsset(assetId, { seat_id: seat.id, lab_id: labId }); toast.success(`Placed at seat ${seat.label}`); onPlaced(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const candidates = [...(infra || []), ...results.filter((r) => !(infra || []).some((i) => i.id === r.id))];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tighter">Place at seat {seat.label}</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9 h-9" placeholder="Search in-store assets..." value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
        </div>
        <div className="max-h-72 overflow-y-auto touch-scroll -mx-1 px-1 divide-y">
          {searching && !candidates.length ? <p className="text-sm text-muted-foreground py-4 text-center">Searching...</p>
            : candidates.length ? candidates.map((a) => (
              <button key={a.id} onClick={() => place(a.id)} disabled={busy}
                className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-black/[0.03] rounded-lg px-2">
                <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[a.status]?.color || "#64748b" }} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm truncate">{a.name}</span>
                  <span className="block text-mono text-[11px] text-muted-foreground">{a.asset_tag}</span>
                </span>
                <Plus className="h-4 w-4 text-cobalt-500" />
              </button>
            )) : <p className="text-sm text-muted-foreground py-4 text-center">No unplaced assets found. Add assets in Inventory first.</p>}
        </div>
        <div className="flex justify-end"><Button variant="ghost" onClick={onClose}><X className="h-4 w-4 mr-1" />Close</Button></div>
      </DialogContent>
    </Dialog>
  );
}
